import { Component, computed, effect, ElementRef, inject, signal, ViewChildren, QueryList } from '@angular/core';
import { Router } from '@angular/router';
import { UwIcon } from '../../ui/icon/uw-icon';
import { UwChip } from '../../ui/chip/uw-chip';
import { UwSearch } from '../../ui/search/uw-search';
import { UwEmptyState } from '../../ui/empty-state/uw-empty-state';
import { UwQueueItem } from '../queue-item/uw-queue-item';
import { QueueStore, QueueFilter } from '../../../core/stores/queue.store';
import { AccountStore } from '../../../core/stores/account.store';
import { CopilotStore } from '../../../core/stores/copilot.store';
import { WorkbenchStore } from '../../../core/stores/workbench.store';
import type { QueueItem } from '../../../core/models/queue.model';

const FILTERS: { id: QueueFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'high', label: 'High Priority' },
  { id: 'judgment', label: 'Judgment' },
  { id: 'straight-through', label: 'Straight-through' },
  { id: 'blocked', label: 'Blocked' },
];

const SORTS = [
  { id: 'ai-priority', label: 'AI priority' },
  { id: 'premium', label: 'Premium' },
  { id: 'confidence', label: 'Match confidence' },
  { id: 'account', label: 'Account name' },
] as const;

@Component({
  selector: 'uw-intent-queue',
  standalone: true,
  imports: [UwIcon, UwChip, UwSearch, UwEmptyState, UwQueueItem],
  template: `
    <div class="flex h-full flex-col" role="listbox" aria-label="Copilot intent queue" (keydown)="onKeydown($event)">
      <div class="mb-3 space-y-2.5">
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-1.5">
            <uw-icon name="bot" [size]="15" class="text-ai" />
            <h2 class="typo-title text-primary">Intent Queue</h2>
            <span class="rounded-full bg-ai-soft px-2 py-0.5 text-[10px] font-bold text-ai">
              {{ items().length }} items
            </span>
          </div>
          <div class="relative">
            <uw-icon name="filter" [size]="12" class="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
            <select
              [value]="queue.sort()"
              (change)="queue.setSort($any($event.target).value)"
              class="cursor-pointer appearance-none rounded-lg border border-line bg-surface py-1.5 pl-7 pr-6 text-[11.5px] font-medium text-secondary focus:border-ai focus:outline-none"
              aria-label="Sort queue"
            >
              @for (sort of SORTS; track sort.id) {
                <option [value]="sort.id">{{ sort.label }}</option>
              }
            </select>
            <uw-icon name="chevron-down" [size]="12" class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-text-muted" />
          </div>
        </div>

        <div class="flex flex-wrap gap-1.5">
          @for (filter of FILTERS; track filter.id) {
            <uw-chip
              [active]="queue.filter() === filter.id"
              [count]="counts()[filter.id]"
              (clicked)="queue.setFilter(filter.id)"
            >
              {{ filter.label }}
            </uw-chip>
          }
        </div>

        <uw-search [query]="queue.search()" (queryChange)="queue.setSearch($event)" placeholder="Search accounts, products, brokers…" size="sm" />
      </div>

      @if (queue.groups().length === 0) {
        <uw-empty-state title="No matches" message="No queue items match the current filter or search." icon="search" />
      } @else {
        <div class="flex-1 space-y-4 overflow-y-auto pr-1">
          @for (group of queue.groups(); track group.category) {
            <section>
              <div class="mb-1.5 flex items-center gap-2">
                <span class="typo-label text-text-muted">{{ group.label }}</span>
                <span class="rounded-full bg-surface-sunken px-1.5 text-[10px] font-bold text-secondary">{{ group.items.length }}</span>
              </div>
              <div class="space-y-1.5">
                @for (item of group.items; track item.id) {
                  <uw-queue-item
                    #itemRef
                    [item]="item"
                    [active]="queue.activeId() === item.id"
                    (selected)="openItem($event)"
                    (focused)="onFocused($event)"
                  />
                }
              </div>
            </section>
          }
        </div>
      }
    </div>
  `,
})
export class UwIntentQueue {
  protected readonly SORTS = SORTS;
  protected readonly FILTERS = FILTERS;

  private readonly router = inject(Router);
  protected readonly queue = inject(QueueStore);
  private readonly account = inject(AccountStore);
  private readonly copilot = inject(CopilotStore);
  private readonly workbench = inject(WorkbenchStore);

  protected readonly counts = computed<Record<string, number>>(() => {
    const items = this.queue.items();
    const count = (c: QueueFilter) => (c === 'all' ? items.length : items.filter((i) => i.category === c).length);
    return {
      all: count('all'),
      high: count('high'),
      judgment: count('judgment'),
      'straight-through': count('straight-through'),
      blocked: count('blocked'),
    };
  });

  protected readonly items = computed(() => this.queue.groups().flatMap((g) => g.items));

  protected readonly activeIndex = signal(-1);

  @ViewChildren('itemRef', { read: ElementRef }) private readonly itemRefs?: QueryList<ElementRef<HTMLElement>>;

  constructor() {
    effect(() => {
      const idx = this.activeIndex();
      if (idx >= 0 && this.itemRefs) {
        this.itemRefs.get(idx)?.nativeElement.focus();
      }
    });
  }

  protected onFocused(id: string): void {
    this.activeIndex.set(this.items().findIndex((i) => i.id === id));
  }

  protected openItem(item: QueueItem): void {
    this.account.selectAccount(item.accountId);
    this.copilot.setView('account');
    this.workbench.setMode('copilot');
    void this.router.navigate(['/workbench/account', item.accountId]);
  }

  protected onKeydown(event: KeyboardEvent): void {
    const list = this.items();
    if (!list.length) return;
    let next = this.activeIndex();
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        next = next < 0 ? 0 : (next + 1) % list.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        next = next <= 0 ? list.length - 1 : next - 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (next >= 0) this.openItem(list[next]);
        return;
      default:
        return;
    }
    this.activeIndex.set(next);
    this.queue.setActive(list[next].id);
  }
}
